import { TableModal } from "@features/ax/shared/tableModal";
import { logEvent } from "@shared/analytics/analytics";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { TooltipProvider } from "@shared/ui/tooltip";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAssetList } from "../assetRegistry/assets/hooks";
import { getConditionLabel } from "../utils";

interface AssetProps {
  showAssetsModal: boolean;
  setShowAssetsModal: any;
  title: string;
  filters: Record<string, any>;
}

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-100 text-red-800";
  if (score >= 40) return "bg-yellow-100 text-yellow-800";
  return "bg-green-100 text-green-800";
};
const getStatusColor = (status: string) => {
  switch (status) {
    case "In Service":
      return "bg-green-100 text-green-800";
    case "Standby":
      return "bg-yellow-100 text-yellow-800";
    case "Out of Service":
      return "bg-red-100 text-red-800";
    case "Under Maintenance":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};
export const AssetModal: React.FC<AssetProps> = ({
  showAssetsModal,
  setShowAssetsModal,
  title,
  filters,
}) => {
  useEffect(() => {
    logEvent("alerts_tab_viewed");
  }, []);
  const navigate = useNavigate();
  const currency = useCurrency();
  const [page, setPage] = useState(1);
  const { data: assetList } = useAssetList(filters);
  const startItem = (page - 1) * MODAL_PAGE_SIZE + 1;
  const endItem = Math.min(page * MODAL_PAGE_SIZE, assetList?.count);

  const pagination = {
    currentPage: page,
    totalPages: Math.ceil(assetList?.count / MODAL_PAGE_SIZE),
    totalItems: assetList?.count,
    startItem: startItem,
    endItem: endItem,
  };
  const columns: TableColumn[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
      headerTooltip: "The unique identifier for this asset.",
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      headerTooltip: "The common name used to identify this asset.",
    },
    {
      key: "assetTypeDisplay",
      header: "Asset Type",
      type: "text",
      headerTooltip: "The general category this asset belongs to.",
    },
    {
      key: "assetClassDisplay",
      header: "Asset Class",
      type: "text",
      headerTooltip:
        "A more detailed classification for grouping and analysis.",
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip: "The physical location or address of this asset.",
    },
    {
      key: "systemNetwork",
      header: "System/Network",
      type: "text",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {row.systemName ?? row.networkName ?? "NA"}
          </span>
        </div>
      ),
      headerTooltip:
        "The parent system or network this asset is a component of.",
    },
    {
      key: "currentAge",
      header: "Age",
      type: "custom",
      render: (value: number) => `${value} years`,
      headerTooltip: "The age of the asset since its installation date.",
    },
    {
      key: "replacementCost",
      header: "Replacement Cost",
      type: "custom",
      render: (value: number) => `${currency} ${value?.toLocaleString()}`,
      headerTooltip:
        "The estimated capital cost to replace this asset with a new one.",
    },
    {
      key: "lastInspectionDate",
      header: "Last Inspection Date",
      type: "text",
      headerTooltip:
        "The date the last formal inspection was completed for this asset.",
    },
    {
      key: "conditionScores",
      header: "Condition Score",
      type: "custom",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The physical health of the asset, based on the most recent inspection. Higher is better.",
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getRiskColor(value)}>
            {row.riskStatus}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
    },
    {
      key: "statusDisplay",
      header: "Operational Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
        variant: "secondary",
      },
      headerTooltip: "The current operational status of the asset.",
    },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive" as const;
      case "medium":
        return "secondary" as const;
      case "low":
        return "default" as const;
      default:
        return "outline" as const;
    }
  };

  return (
    <TooltipProvider>
      <TableModal
        isOpen={showAssetsModal}
        title={title}
        onClose={() => setShowAssetsModal(false)}
        columns={columns}
        data={assetList?.results ?? []}
        loading={false}
        searchPlaceholder="Search assets..."
        // pagination={pagination}
        onPageChange={setPage}
      />
    </TooltipProvider>
  );
};
